// frontend/src/components/Dashboard/PayoutComponent.jsx - CLEANED
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWallet,
  faDollarSign,
  faSpinner,
  faPlus,
  faTimes,
  faCheckCircle,
  faExclamationTriangle,
  faBank,
  faMobileAlt,
  faBuilding,
  faPhone,
  faHistory,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { getPayoutHistory, requestPayout, getPayoutStats } from "../../api";
import { useCurrency } from "../../context/CurrencyContext";

const PayoutComponent = () => {
  const { formatAmount } = useCurrency();
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState({
    amount: "",
    method: "bank_transfer",
    accountDetails: "",
  });

  const payoutMethods = [
    { value: "bank_transfer", label: "Bank Transfer", icon: faBank },
    { value: "mobile_money", label: "Mobile Money", icon: faMobileAlt },
    { value: "orange_money", label: "Orange Money", icon: faMobileAlt },
    { value: "afrimoney", label: "Afrimoney", icon: faMobileAlt },
  ];

  const fetchPayoutData = async () => {
    setLoading(true);
    try {
      const [historyRes, statsRes] = await Promise.all([
        getPayoutHistory(),
        getPayoutStats(),
      ]);
      setPayouts(historyRes.data?.data || []);
      setStats(statsRes.data || {});
    } catch (error) {
      console.error("Error fetching payout data:", error);
      setError("Failed to load payout data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayoutData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");

    const amount = parseFloat(formData.amount);
    if (!amount || amount <= 0) {
      setError("Please enter a valid amount");
      setSubmitting(false);
      return;
    }

    if (!formData.method || !formData.accountDetails) {
      setError("Please fill in all fields");
      setSubmitting(false);
      return;
    }

    try {
      await requestPayout({
        amount: amount,
        method: formData.method,
        accountDetails: formData.accountDetails,
      });

      setSuccess("Payout requested successfully!");
      setFormData({
        amount: "",
        method: "bank_transfer",
        accountDetails: "",
      });

      await fetchPayoutData();
      setTimeout(() => setShowModal(false), 1500);
    } catch (error) {
      console.error("Error requesting payout:", error);
      setError(error.response?.data?.message || "Failed to request payout");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: "Pending",
      processing: "Processing",
      completed: "Completed",
      failed: "Failed",
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "#f59e0b",
      processing: "#3b82f6",
      completed: "#16a34a",
      failed: "#dc2626",
    };
    return colors[status] || "#5A5A5A";
  };

  const getMethodLabel = (method) => {
    const found = payoutMethods.find((m) => m.value === method);
    return found?.label || method.replace("_", " ").toUpperCase();
  };

  const formatCurrency = (amount) => formatAmount(amount, true);

  if (loading) {
    return (
      <div className="payout-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <span>Loading payout data...</span>
      </div>
    );
  }

  return (
    <div className="payout-container">
      {/* Header */}
      <div className="payout-header">
        <h3>
          <FontAwesomeIcon icon={faWallet} /> Payout History
        </h3>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          <FontAwesomeIcon icon={faPlus} /> Request Payout
        </button>
      </div>

      {/* Error/Success */}
      {error && (
        <div className="payout-alert error">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{error}</span>
          <button onClick={() => setError("")}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}
      {success && (
        <div className="payout-alert success">
          <FontAwesomeIcon icon={faCheckCircle} />
          <span>{success}</span>
          <button onClick={() => setSuccess("")}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="payout-stats-grid">
        <div className="payout-stat-card">
          <div className="payout-stat-icon">
            <FontAwesomeIcon icon={faDollarSign} />
          </div>
          <div className="payout-stat-info">
            <div className="payout-stat-value">
              {formatCurrency(stats.totalEarnings || 0)}
            </div>
            <div className="payout-stat-label">Total Earnings</div>
          </div>
        </div>
        <div className="payout-stat-card pending">
          <div className="payout-stat-icon">
            <FontAwesomeIcon icon={faClock} />
          </div>
          <div className="payout-stat-info">
            <div className="payout-stat-value">{stats.pendingPayouts || 0}</div>
            <div className="payout-stat-label">Pending Payouts</div>
          </div>
        </div>
        <div className="payout-stat-card completed">
          <div className="payout-stat-icon">
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          <div className="payout-stat-info">
            <div className="payout-stat-value">
              {formatCurrency(stats.totalPaid || 0)}
            </div>
            <div className="payout-stat-label">Total Paid</div>
          </div>
        </div>
      </div>

      {/* Payout History Table */}
      <div className="payout-history">
        <h4>
          <FontAwesomeIcon icon={faHistory} /> History
        </h4>
        <div className="table-responsive">
          <table className="payout-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Account</th>
                <th>Status</th>
                <th>Transaction ID</th>
              </tr>
            </thead>
            <tbody>
              {payouts.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data">
                    <FontAwesomeIcon icon={faWallet} />
                    <p>No payout history yet.</p>
                    <span>Request a payout to get started.</span>
                  </td>
                </tr>
              ) : (
                payouts.map((payout) => (
                  <tr key={payout._id}>
                    <td>{new Date(payout.createdAt).toLocaleDateString()}</td>
                    <td className="amount">{formatCurrency(payout.amount)}</td>
                    <td>{getMethodLabel(payout.method)}</td>
                    <td className="account-details">{payout.accountDetails}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ background: getStatusColor(payout.status) }}
                      >
                        {getStatusLabel(payout.status)}
                      </span>
                    </td>
                    <td>{payout.transactionId || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Request Payout Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FontAwesomeIcon icon={faWallet} /> Request Payout
              </h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="payout-form">
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faDollarSign} /> Amount *
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="Enter amount"
                  min="1"
                  step="0.01"
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faBank} /> Payout Method *
                </label>
                <select
                  name="method"
                  value={formData.method}
                  onChange={handleChange}
                  required
                >
                  {payoutMethods.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faBuilding} /> Account Details *
                </label>
                <input
                  type="text"
                  name="accountDetails"
                  value={formData.accountDetails}
                  onChange={handleChange}
                  placeholder={
                    formData.method === "bank_transfer"
                      ? "Bank: SLB, Account: 1234567890"
                      : "Phone: +232 76 123456"
                  }
                  required
                />
              </div>

              {error && (
                <div className="form-error">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>{error}</span>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin /> Processing...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} /> Request Payout
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutComponent;
